import React from "react";
import { nodeGroupApi, scriptTransitionsApi } from "../../services/api";
import type { NodeGroupTransition } from "../../types/node_group";
import type {
  ConditionRule,
  ConditionRuleCreate,
  ConditionRuleUpdate,
} from "../../types/node_group";
import { Trash2, ChevronDown, ChevronUp } from "lucide-react";
import ValuesListEditor from "../ui/ValuesListEditor";
import { Textarea } from "../ui/Input";

interface Props {
  groupId?: number | null;
  scriptId?: number | null;
  currentNodeId: number;
  nodes: Array<{ id: number; node_name: string; script_id?: number | null }>;
  transitions: NodeGroupTransition[];
  onChange: (next: NodeGroupTransition[]) => void;
  // New: allow customizing section title
  title?: string;
  // New: read-only mode
  readOnly?: boolean;
}

export default function NodeTransitionsSection({
  groupId,
  scriptId,
  currentNodeId,
  nodes,
  transitions,
  onChange,
  title = "Transitions",
  readOnly = false,
}: Props) {
  const related = React.useMemo(
    () =>
      transitions
        .filter((t) => t.current_node_id === currentNodeId)
        .filter((t) => (scriptId ? (t as any).script_id != null : true)),
    [transitions, currentNodeId, scriptId],
  );
  const [isBusy, setIsBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [collapsed, setCollapsed] = React.useState(true);
  const [expanded, setExpanded] = React.useState<Record<number, boolean>>({});
  const [rulesByTransition, setRulesByTransition] = React.useState<
    Record<number, ConditionRule[]>
  >({});

  React.useEffect(() => {
    if (readOnly) {
      setCollapsed(false);
      // Expand all transitions panels
      setExpanded((prev) => {
        const next: Record<number, boolean> = {};
        for (const tr of related) next[tr.id as number] = true;
        return next;
      });
    }
  }, [readOnly, related]);

  const toErrorMessage = (e: any): string => {
    try {
      const detail = e?.response?.data?.detail ?? e?.message ?? e;
      if (Array.isArray(detail))
        return detail.map((d: any) => d?.msg || JSON.stringify(d)).join("; ");
      if (typeof detail === "object")
        return detail?.msg || JSON.stringify(detail);
      return String(detail || "Unexpected error");
    } catch {
      return "Unexpected error";
    }
  };

  const toggleExpanded = async (transitionId: number) => {
    setExpanded((prev) => ({ ...prev, [transitionId]: !prev[transitionId] }));
    if (!expanded[transitionId]) {
      if (!groupId && !scriptId) {
        setRulesByTransition((prev) => ({ ...prev, [transitionId]: [] }));
        return;
      }
      try {
        if (groupId) {
          const res = await nodeGroupApi.listConditionRules(
            groupId,
            transitionId,
          );
          setRulesByTransition((prev) => ({
            ...prev,
            [transitionId]: res.rules,
          }));
        } else if (scriptId) {
          const res = await scriptTransitionsApi.listConditionRules(
            scriptId,
            transitionId,
          );
          setRulesByTransition((prev) => ({
            ...prev,
            [transitionId]: res.rules,
          }));
        }
      } catch (e: any) {
        if (e?.response?.status === 404)
          setRulesByTransition((prev) => ({ ...prev, [transitionId]: [] }));
        else setError(toErrorMessage(e));
      }
    }
  };

  const create = async () => {
    const nextNodeId = nodes.find((n) => n.id !== currentNodeId)?.id;
    if (!nextNodeId) return;
    setIsBusy(true);
    setError(null);
    try {
      if (groupId) {
        const row = await nodeGroupApi.createTransition(groupId, {
          current_node_id: currentNodeId,
          next_node_id: nextNodeId,
        });
        onChange([...transitions, row]);
      } else if (scriptId) {
        const row = await scriptTransitionsApi.createTransition(scriptId, {
          current_node_id: currentNodeId,
          next_node_id: nextNodeId,
        });
        onChange([...transitions, row as any]);
      } else {
        // No backend context; do optimistic local add with temp id
        const tempId =
          Math.max(0, ...transitions.map((t) => Number(t.id) || 0)) + 1;
        onChange([
          ...transitions,
          {
            id: tempId as any,
            current_node_id: currentNodeId,
            next_node_id: nextNodeId,
          } as any,
        ]);
      }
    } catch (e: any) {
      setError(toErrorMessage(e) || "Failed to create transition");
    } finally {
      setIsBusy(false);
    }
  };

  const update = async (
    id: number,
    payload: Partial<Pick<NodeGroupTransition, "next_node_id">>,
  ) => {
    setIsBusy(true);
    setError(null);
    try {
      if (groupId) {
        const row = await nodeGroupApi.updateTransition(groupId, id, {
          next_node_id: payload.next_node_id!,
        });
        onChange(transitions.map((t) => (t.id === id ? row : t)));
      } else if (scriptId) {
        const row = await scriptTransitionsApi.updateTransition(scriptId, id, {
          next_node_id: payload.next_node_id!,
        });
        onChange(transitions.map((t) => (t.id === id ? (row as any) : t)));
      } else {
        onChange(
          transitions.map((t) =>
            t.id === id
              ? ({
                  ...t,
                  next_node_id: payload.next_node_id ?? t.next_node_id,
                } as any)
              : t,
          ),
        );
      }
    } catch (e: any) {
      setError(toErrorMessage(e) || "Failed to update transition");
    } finally {
      setIsBusy(false);
    }
  };

  const remove = async (id: number) => {
    setIsBusy(true);
    setError(null);
    try {
      if (groupId) {
        await nodeGroupApi.deleteTransition(groupId, id);
        onChange(transitions.filter((t) => t.id !== id));
        setRulesByTransition((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      } else if (scriptId) {
        await scriptTransitionsApi.deleteTransition(scriptId, id);
        onChange(transitions.filter((t) => t.id !== id));
      } else {
        onChange(transitions.filter((t) => t.id !== id));
      }
    } catch (e: any) {
      setError(toErrorMessage(e) || "Failed to delete transition");
    } finally {
      setIsBusy(false);
    }
  };

  // Helpers for local (group-less) rules using transitions.capture_rules
  const getLocalRules = (transitionId: number): ConditionRule[] => {
    const tr = transitions.find((t) => t.id === transitionId) as any;
    const rules = (tr?.capture_rules || []) as ConditionRule[];
    return Array.isArray(rules) ? rules : [];
  };
  const setLocalRules = (transitionId: number, rules: ConditionRule[]) => {
    onChange(
      transitions.map((t: any) =>
        t.id === transitionId ? { ...t, capture_rules: rules } : t,
      ),
    );
  };

  const addRule = async (transitionId: number) => {
    setIsBusy(true);
    setError(null);
    try {
      if (!groupId && !scriptId) {
        const next: ConditionRule = {
          id: Date.now(),
          type: "any_input" as any,
          values: null,
          sql_query: null,
          db_name: null,
          logical_operator: undefined as any,
          use_logical_not: false as any,
          user_input_node_id: null as any,
        } as any;
        setLocalRules(transitionId, [...getLocalRules(transitionId), next]);
        return;
      }
      const payload: ConditionRuleCreate = { type: "any_input" };
      if (groupId) {
        const created = await nodeGroupApi.createConditionRule(
          groupId,
          transitionId,
          payload,
        );
        setRulesByTransition((prev) => ({
          ...prev,
          [transitionId]: [...(prev[transitionId] || []), created],
        }));
      } else if (scriptId) {
        const created = await scriptTransitionsApi.createConditionRule(
          scriptId,
          transitionId,
          payload,
        );
        setRulesByTransition((prev) => ({
          ...prev,
          [transitionId]: [...(prev[transitionId] || []), created as any],
        }));
      }
    } catch (e: any) {
      setError(toErrorMessage(e) || "Failed to add condition rule");
    } finally {
      setIsBusy(false);
    }
  };

  const updateRule = async (
    transitionId: number,
    rule: ConditionRule,
    changes: ConditionRuleUpdate,
  ) => {
    setIsBusy(true);
    setError(null);
    try {
      if (!groupId && !scriptId) {
        const current = getLocalRules(transitionId);
        const updated: ConditionRule = { ...rule, ...changes } as any;
        setLocalRules(
          transitionId,
          current.map((r) =>
            (r as any).id === (rule as any).id ? updated : r,
          ),
        );
        return;
      }
      if (groupId) {
        const updated = await nodeGroupApi.updateConditionRule(
          groupId,
          transitionId,
          rule.id,
          changes,
        );
        setRulesByTransition((prev) => ({
          ...prev,
          [transitionId]: (prev[transitionId] || []).map((r) =>
            r.id === rule.id ? updated : r,
          ),
        }));
      } else if (scriptId) {
        const updated = await scriptTransitionsApi.updateConditionRule(
          scriptId,
          transitionId,
          rule.id,
          changes,
        );
        setRulesByTransition((prev) => ({
          ...prev,
          [transitionId]: (prev[transitionId] || []).map((r) =>
            r.id === rule.id ? (updated as any) : r,
          ),
        }));
      }
    } catch (e: any) {
      setError(toErrorMessage(e) || "Failed to update condition rule");
    } finally {
      setIsBusy(false);
    }
  };

  const deleteRule = async (transitionId: number, ruleId: number) => {
    setIsBusy(true);
    setError(null);
    try {
      if (!groupId && !scriptId) {
        const current = getLocalRules(transitionId);
        setLocalRules(
          transitionId,
          current.filter((r) => (r as any).id !== ruleId),
        );
        return;
      }
      if (groupId) {
        await nodeGroupApi.deleteConditionRule(groupId, transitionId, ruleId);
        setRulesByTransition((prev) => ({
          ...prev,
          [transitionId]: (prev[transitionId] || []).filter(
            (r) => r.id !== ruleId,
          ),
        }));
      } else if (scriptId) {
        await scriptTransitionsApi.deleteConditionRule(
          scriptId,
          transitionId,
          ruleId,
        );
        setRulesByTransition((prev) => ({
          ...prev,
          [transitionId]: (prev[transitionId] || []).filter(
            (r) => r.id !== ruleId,
          ),
        }));
      }
    } catch (e: any) {
      setError(toErrorMessage(e) || "Failed to delete condition rule");
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg border border-gray-200 relative">
      <div className={readOnly ? "pointer-events-none" : undefined}>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            className="p-1 rounded hover:bg-gray-100"
            aria-label="Toggle section"
          >
            {collapsed ? (
              <ChevronDown className="h-5 w-5" />
            ) : (
              <ChevronUp className="h-5 w-5" />
            )}
          </button>
        </div>
        {!collapsed && (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-3">
              {error && <div className="text-xs text-red-600">{error}</div>}
              <button
                type="button"
                onClick={create}
                disabled={isBusy || nodes.length <= 1}
                className="px-2 py-1 text-xs bg-blue-600 text-white rounded disabled:opacity-50"
              >
                Add Transition
              </button>
            </div>
            {related.length === 0 ? (
              <p className="text-sm text-gray-500">
                No transitions from this node.
              </p>
            ) : (
              <div className="space-y-2">
                {related.map((tr) => (
                  <div key={tr.id} className="border rounded">
                    <div className="flex items-center gap-2 p-2">
                      <span className="text-sm text-gray-600">→</span>
                      <SearchableNodeSelect
                        value={tr.next_node_id}
                        nodes={nodes}
                        excludeId={currentNodeId}
                        onChange={(val) => update(tr.id, { next_node_id: val })}
                      />
                      <button
                        type="button"
                        onClick={() => toggleExpanded(tr.id)}
                        className="ml-auto p-1 rounded hover:bg-gray-100"
                        aria-label="Toggle rules"
                      >
                        {expanded[tr.id] ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(tr.id as number)}
                        className="p-1 rounded hover:bg-red-50 text-red-600"
                        aria-label="Delete transition"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    {expanded[tr.id] && (
                      <div className="p-3 border-t bg-gray-50 space-y-2">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-medium">
                            Condition Rules
                          </h4>
                          <button
                            type="button"
                            className="px-2 py-1 text-xs bg-blue-600 text-white rounded"
                            onClick={() => addRule(tr.id as number)}
                          >
                            Add Rule
                          </button>
                        </div>
                        {(groupId || scriptId
                          ? rulesByTransition[tr.id] || []
                          : getLocalRules(tr.id)
                        ).length === 0 ? (
                          <div className="text-xs text-gray-600">No rules</div>
                        ) : (
                          <div className="space-y-2">
                            {(groupId || scriptId
                              ? rulesByTransition[tr.id] || []
                              : getLocalRules(tr.id)
                            ).map((rule: any) => (
                              <ConditionRuleEditor
                                key={rule.id}
                                rule={rule}
                                nodes={nodes}
                                onChange={(chg) =>
                                  updateRule(tr.id as number, rule, chg)
                                }
                                onDelete={() =>
                                  deleteRule(tr.id as number, rule.id as number)
                                }
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ConditionRuleEditor({
  rule,
  nodes,
  onChange,
  onDelete,
}: {
  rule: ConditionRule;
  nodes: Array<{ id: number; node_name: string; script_id?: number | null }>;
  onChange: (c: ConditionRuleUpdate) => void;
  onDelete: () => void;
}) {
  const [local, setLocal] = React.useState<ConditionRule>({ ...rule });
  React.useEffect(() => {
    setLocal({ ...rule });
  }, [rule.id]);

  const apply = (chg: Partial<ConditionRule>) => {
    const next = { ...local, ...chg } as ConditionRule;
    setLocal(next);
  };

  const save = () => {
    const payload: ConditionRuleUpdate = {
      type: local.type,
      values: local.values || null,
      sql_query: local.sql_query || null,
      db_name: local.db_name || null,
      logical_operator: local.logical_operator,
      use_logical_not: local.use_logical_not,
      user_input_node_id: local.user_input_node_id || null,
    };
    onChange(payload);
  };

  return (
    <div className="bg-white border rounded p-2">
      <div className="grid grid-cols-2 gap-2">
        <label className="text-xs text-gray-600">
          Type
          <select
            className="mt-1 w-full border rounded px-2 py-1 text-sm"
            value={local.type}
            onChange={(e) => apply({ type: e.target.value })}
          >
            <option value="any_input">any_input</option>
            <option value="auto">auto</option>
            <option value="input_in">input_in</option>
            <option value="input_not_in">input_not_in</option>
            <option value="sql">sql</option>
            <option value="daytime">daytime</option>
            <option value="quiet_periods">quiet_periods</option>
            <option value="variable_in">variable_in</option>
            <option value="variable_not_in">variable_not_in</option>
            <option value="variable_is_falsy">variable_is_falsy</option>
            <option value="variable_is_truthy">variable_is_truthy</option>
            <option value="variable_is_null">variable_is_null</option>
            <option value="variable_is_not_null">variable_is_not_null</option>
            <option value="variables_expression">variables_expression</option>
            <option value="entrypoint">entrypoint</option>
            <option value="timer">timer</option>
          </select>
        </label>
        <label className="text-xs text-gray-600">
          Logical operator
          <select
            className="mt-1 w-full border rounded px-2 py-1 text-sm"
            value={local.logical_operator || ""}
            onChange={(e) =>
              apply({ logical_operator: (e.target.value || undefined) as any })
            }
          >
            <option value="">AND (default)</option>
            <option value="OR">OR</option>
          </select>
        </label>
        <label className="text-xs text-gray-600 flex items-center gap-2">
          Not
          <input
            type="checkbox"
            className="mt-1"
            checked={!!local.use_logical_not}
            onChange={(e) => apply({ use_logical_not: e.target.checked })}
          />
        </label>
        {(local.type === "any_input" ||
          local.type === "input_in" ||
          local.type === "input_not_in") && (
          <label className="text-xs text-gray-600">
            User input node
            <div className="mt-1">
              <SearchableNodeSelect
                value={local.user_input_node_id || 0}
                nodes={nodes}
                onChange={(val) => apply({ user_input_node_id: val })}
              />
            </div>
          </label>
        )}
      </div>

      {/* Values and SQL specialized editors */}
      {(local.type === "input_in" || local.type === "input_not_in") && (
        <div className="mt-2">
          <ValuesListEditor
            values={
              Array.isArray(local.values)
                ? (local.values as any[])
                : Array.isArray((local.values as any)?.values)
                  ? (local.values as any).values || []
                  : []
            }
            onChange={(newValues) => apply({ values: newValues })}
            placeholder="Enter value"
            label="Values"
          />
        </div>
      )}

      {local.type === "variable_in" || local.type === "variable_not_in" ? (
        <div className="space-y-2 mt-2">
          <label className="text-xs text-gray-600">
            Variable name
            <input
              className="mt-1 w-full border rounded px-2 py-1 text-sm"
              value={(local.values as any)?.var_name || ""}
              onChange={(e) =>
                apply({
                  values: {
                    ...(local.values as any),
                    var_name: e.target.value,
                    values: (local.values as any)?.values || [],
                  },
                })
              }
            />
          </label>
          <ValuesListEditor
            values={
              Array.isArray((local.values as any)?.values)
                ? (local.values as any).values || []
                : []
            }
            onChange={(newValues) =>
              apply({
                values: {
                  ...(local.values as any),
                  values: newValues,
                },
              })
            }
            placeholder="Enter value"
            label="Allowed values"
          />
        </div>
      ) : null}

      {local.type === "variable_is_falsy" ||
      local.type === "variable_is_truthy" ||
      local.type === "variable_is_null" ||
      local.type === "variable_is_not_null" ? (
        <label className="block text-xs text-gray-600 mt-2">
          Variable name
          <input
            className="mt-1 w-full border rounded px-2 py-1 text-sm"
            value={(local.values as any)?.var_name || ""}
            onChange={(e) =>
              apply({
                values: { ...(local.values as any), var_name: e.target.value },
              })
            }
          />
        </label>
      ) : null}

      {local.type === "variables_expression" ? (
        <div className="mt-2">
          <Textarea
            label="Expression"
            className="text-sm"
            rows={3}
            value={typeof local.values === "string" ? local.values : ""}
            onChange={(e) => apply({ values: e.target.value })}
            variant="rounded-md"
            showLabel={true}
          />
        </div>
      ) : null}

      {local.type === "quiet_periods" ? (
        <div className="grid grid-cols-2 gap-2 mt-2">
          <label className="text-xs text-gray-600">
            Names (comma-separated)
            <input
              className="mt-1 w-full border rounded px-2 py-1 text-sm"
              value={
                Array.isArray((local.values as any)?.names)
                  ? ((local.values as any).names || []).join(",")
                  : ""
              }
              onChange={(e) => {
                const parts = e.target.value
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean);
                apply({ values: { ...(local.values as any), names: parts } });
              }}
            />
          </label>
          <label className="text-xs text-gray-600">
            Tags (comma-separated)
            <input
              className="mt-1 w-full border rounded px-2 py-1 text-sm"
              value={
                Array.isArray((local.values as any)?.tags)
                  ? ((local.values as any).tags || []).join(",")
                  : ""
              }
              onChange={(e) => {
                const parts = e.target.value
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean);
                apply({ values: { ...(local.values as any), tags: parts } });
              }}
            />
          </label>
        </div>
      ) : null}

      {local.type === "daytime" ? (
        <div className="grid grid-cols-2 gap-2 mt-2">
          <label className="text-xs text-gray-600">
            From (HH:MM:SS)
            <input
              className="mt-1 w-full border rounded px-2 py-1 text-sm"
              value={(local.values as any)?.from || ""}
              onChange={(e) =>
                apply({
                  values: { ...(local.values as any), from: e.target.value },
                })
              }
            />
          </label>
          <label className="text-xs text-gray-600">
            To (HH:MM:SS)
            <input
              className="mt-1 w-full border rounded px-2 py-1 text-sm"
              value={(local.values as any)?.to || ""}
              onChange={(e) =>
                apply({
                  values: { ...(local.values as any), to: e.target.value },
                })
              }
            />
          </label>
          <label className="text-xs text-gray-600">
            Weekdays (0-6, comma-separated)
            <input
              className="mt-1 w-full border rounded px-2 py-1 text-sm"
              value={
                Array.isArray((local.values as any)?.weekdays)
                  ? ((local.values as any).weekdays || []).join(",")
                  : ""
              }
              onChange={(e) => {
                const parts = e.target.value
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean)
                  .map((x) => Number(x))
                  .filter((x) => Number.isFinite(x));
                apply({
                  values: { ...(local.values as any), weekdays: parts },
                });
              }}
            />
          </label>
          <label className="text-xs text-gray-600">
            Timezone (IANA)
            <input
              className="mt-1 w-full border rounded px-2 py-1 text-sm"
              value={(local.values as any)?.timezone || ""}
              onChange={(e) =>
                apply({
                  values: {
                    ...(local.values as any),
                    timezone: e.target.value,
                  },
                })
              }
            />
          </label>
        </div>
      ) : null}

      {local.type === "sql" ? (
        <div className="grid grid-cols-2 gap-2 mt-2">
          <label className="text-xs text-gray-600">
            DB name
            <input
              className="mt-1 w-full border rounded px-2 py-1 text-sm"
              value={local.db_name || ""}
              onChange={(e) => apply({ db_name: e.target.value })}
            />
          </label>
          <div className="col-span-2">
            <Textarea
              label="SQL query"
              className="text-sm"
              rows={3}
              value={local.sql_query || ""}
              onChange={(e) => apply({ sql_query: e.target.value })}
              variant="rounded-md"
              showLabel={true}
            />
          </div>
        </div>
      ) : null}

      {local.type === "entrypoint" ? (
        <div className="grid grid-cols-2 gap-2 mt-2">
          <label className="text-xs text-gray-600">
            Came from nodes
            <select
              multiple
              className="mt-1 w-full border rounded px-2 py-1 text-sm h-28"
              value={
                Array.isArray((local.values as any)?.came_from_nodes_id)
                  ? ((local.values as any).came_from_nodes_id || []).map(
                      (n: any) => String(n),
                    )
                  : []
              }
              onChange={(e) => {
                const options = Array.from(e.target.selectedOptions)
                  .map((o) => Number(o.value))
                  .filter((n) => Number.isFinite(n));
                apply({
                  values: {
                    ...(local.values as any),
                    came_from_nodes_id: options,
                    entrypoint_node_id:
                      (local.values as any)?.entrypoint_node_id || null,
                  },
                });
              }}
            >
              {(window as any)?.__nodes_for_select || []}
            </select>
          </label>
          <label className="text-xs text-gray-600">
            Entrypoint node
            <select
              className="mt-1 w-full border rounded px-2 py-1 text-sm"
              value={String((local.values as any)?.entrypoint_node_id || "")}
              onChange={(e) =>
                apply({
                  values: {
                    ...(local.values as any),
                    entrypoint_node_id: e.target.value
                      ? Number(e.target.value)
                      : null,
                  },
                })
              }
            >
              <option value="">Select…</option>
              {(window as any)?.__nodes_for_select || []}
            </select>
          </label>
        </div>
      ) : null}

      {local.type === "timer" ? (
        <div className="grid grid-cols-2 gap-2 mt-2">
          <label className="text-xs text-gray-600">
            Variable to check (path)
            <input
              className="mt-1 w-full border rounded px-2 py-1 text-sm"
              value={(local.values as any)?.variable_to_check || ""}
              onChange={(e) =>
                apply({
                  values: {
                    ...(local.values as any),
                    variable_to_check: e.target.value,
                  },
                })
              }
            />
          </label>
          <label className="text-xs text-gray-600">
            Trigger seconds
            <input
              className="mt-1 w-full border rounded px-2 py-1 text-sm"
              value={String((local.values as any)?.trigger_seconds ?? "")}
              onChange={(e) => {
                const v = e.target.value;
                const num = Number(v);
                apply({
                  values: {
                    ...(local.values as any),
                    trigger_seconds: Number.isFinite(num) ? num : v,
                  },
                });
              }}
            />
          </label>
        </div>
      ) : null}

      <div className="flex items-center gap-2 mt-3">
        <button
          type="button"
          className="px-2 py-1 text-xs bg-blue-600 text-white rounded"
          onClick={save}
        >
          Save
        </button>
        <button
          type="button"
          className="px-2 py-1 text-xs border rounded text-red-600"
          onClick={onDelete}
        >
          Delete
        </button>
      </div>
    </div>
  );
}

function SearchableNodeSelect({
  value,
  nodes,
  excludeId,
  onChange,
}: {
  value: number;
  nodes: Array<{ id: number; node_name: string; script_id?: number | null }>;
  excludeId?: number;
  onChange: (val: number) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const ref = React.useRef<HTMLDivElement | null>(null);
  const selected = nodes.find((n) => n.id === value) || null;
  const options = React.useMemo(
    () => nodes.filter((n) => n.id !== excludeId),
    [nodes, excludeId],
  );
  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((n) => n.node_name.toLowerCase().includes(q));
  }, [options, query]);

  React.useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as unknown as HTMLElement))
        setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="px-2 py-1 border rounded w-56 text-left bg-white"
      >
        {selected ? selected.node_name : "Select node..."}
      </button>
      {open && (
        <div className="absolute z-10 mt-1 w-72 bg-white border rounded shadow">
          <div className="p-2 border-b">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search node name..."
              className="w-full px-2 py-1 border rounded"
            />
          </div>
          <div className="max-h-60 overflow-auto">
            {filtered.map((n) => (
              <button
                key={n.id}
                type="button"
                className={`w-full text-left px-3 py-2 hover:bg-gray-50 ${n.id === value ? "bg-blue-50" : ""} ${n.script_id == null ? "bg-yellow-50" : ""}`}
                onClick={() => {
                  onChange(n.id);
                  setOpen(false);
                }}
              >
                {n.node_name}
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="px-3 py-2 text-sm text-gray-500">No results</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
